import React, { useState } from "react";
import { Navigate, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import Divider from '@mui/material/Divider';
import { suportaBiometria } from '../../utils/biometria';
import { useAuth } from '../../AuthContext';
import api from '../../api';
import tokens from '../../theme/tokens';

const emailRegex = /^[A-Za-z0-9+_.-]+@(.+)$/;

const Login = () => {
  const { isAuthenticated, login, loginBiometria, userLogado } = useAuth();
  const [temBiometria] = useState(suportaBiometria());
  const [entrandoBiometria, setEntrandoBiometria] = useState(false);

  const handleBiometria = async () => {
    setEntrandoBiometria(true);
    const response = await loginBiometria();
    if (!response.isAuthenticated) notify(response.message);
    setEntrandoBiometria(false);
  };
  const navigate = useNavigate();
  const location = useLocation();

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [resetEmail, setResetEmail] = useState('');
  const [dialogOpenReset, setDialogOpenReset] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [severity, setSeverity] = useState('success');
  const [openMessage, setOpenMessage] = useState(false);
  const [message, setMessage] = useState('');

  const notify = (text, type = 'error') => {
    setMessage(text);
    setSeverity(type);
    setOpenMessage(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail.match(emailRegex)) return notify("E-mail inválido");
    if (!loginPassword) return notify("Senha obrigatória");

    setLoading(true);
    try {
      const response = await login(loginEmail, loginPassword);
      if (!response.isAuthenticated) notify(response.message);
    } catch (error) {
      notify('Erro inesperado');
    }
    setLoading(false);
  };

  const resetPassword = async () => {
    setSubmitting(true);
    try {
      const response = await api.post('user/reset', { email: resetEmail });
      if (response.status === 200) {
        setDialogOpenReset(false);
        notify("Enviamos o link de redefinição para o seu e-mail.", 'success');
      } else {
        notify(response.data.error);
      }
    } catch (error) {
      notify(error.response?.data?.error || error.response?.data?.message || 'Ocorreu um erro');
    }
    setSubmitting(false);
  };

  if (isAuthenticated) {
    // Veio de uma tela protegida (ex.: link da tag NFC): volta para ela
    const voltarPara = location.state?.from;
    return <Navigate replace to={userLogado?.perfil === 'Arbitro' ? '/arbitro' : userLogado?.perfil === 'Goleiro' ? '/goleiro' : voltarPara || '/painel'} />;
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, bgcolor: 'background.paper' }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          color: '#fff',
          background: `radial-gradient(circle at 70% 30%, rgba(215,38,61,0.35), transparent 50%), linear-gradient(135deg, ${tokens.color.ink}, ${tokens.color.inkSoft})`,
        }}
      >
        <Typography component={RouterLink} to="/" sx={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: '1.5rem', color: '#fff', textDecoration: 'none' }}>
          RIVIERA <Box component="span" sx={{ color: tokens.color.brandLight }}>FC</Box>
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ width: 260, height: 260, mx: 'auto', borderRadius: '50%', bgcolor: '#fff', display: 'grid', placeItems: 'center', mb: 4 }}>
            <Box component="img" src="/logo_rfc.png" alt="Riviera FC" sx={{ width: '80%' }} />
          </Box>
          <Typography variant="h3" sx={{ fontSize: '2.6rem' }}>Bem-vindo de volta</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>Confirme presença, acompanhe o torneio e mantenha suas mensalidades em dia.</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>Riviera Boa Viagem</Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 3, sm: 6 } }}>
        <Box component="form" onSubmit={handleLogin} noValidate sx={{ width: '100%', maxWidth: 400 }}>
          <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 3 }}>
            <Box component="img" src="/logo_rfc.png" alt="Riviera FC" sx={{ width: 110 }} />
          </Box>
          <Typography variant="h4">Entrar</Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>Use o e-mail e a senha do seu cadastro.</Typography>

          <Stack spacing={2.5}>
            <TextField label="E-mail" type="email" autoComplete="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} fullWidth autoFocus />
            <TextField
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="Mostrar senha" onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ textAlign: 'right', mt: '-8px !important' }}>
              <Link component="button" type="button" variant="body2" onClick={() => { setResetEmail(loginEmail); setDialogOpenReset(true); }}>
                Esqueci a senha
              </Link>
            </Box>
            <Button type="submit" variant="contained" size="large" disabled={loading} sx={{ py: 1.4 }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
            </Button>
            {temBiometria && (
              <>
                <Divider sx={{ color: 'text.secondary', fontSize: 13 }}>ou</Divider>
                <Button variant="outlined" size="large" startIcon={entrandoBiometria ? <CircularProgress size={20} /> : <FingerprintRoundedIcon />} onClick={handleBiometria} disabled={entrandoBiometria} sx={{ py: 1.3 }}>
                  Entrar com biometria
                </Button>
              </>
            )}
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            Ainda não tem cadastro?{' '}
            <Link component="button" type="button" variant="body2" fontWeight={600} onClick={() => navigate('/cadastro')}>
              Criar conta
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            <Link component={RouterLink} to="/arbitro/login" color="text.secondary">Acesso do árbitro</Link>
            {' · '}
            <Link component={RouterLink} to="/goleiro/login" color="text.secondary">Acesso do goleiro</Link>
          </Typography>
        </Box>
      </Box>

      <Snackbar open={openMessage} onClose={() => setOpenMessage(false)} autoHideDuration={6000} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert elevation={6} variant="filled" severity={severity} onClose={() => setOpenMessage(false)} sx={{ width: '100%' }}>
          {message}
        </MuiAlert>
      </Snackbar>

      <Dialog open={dialogOpenReset} onClose={() => setDialogOpenReset(false)} fullWidth maxWidth="xs">
        <DialogTitle>Redefinir senha</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>Informe o e-mail do cadastro para receber o link de redefinição.</DialogContentText>
          <TextField label="E-mail" type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} fullWidth autoFocus />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpenReset(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={resetPassword} disabled={submitting}>Enviar link</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
